import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Repeat, Plus, Edit2, Trash2, RefreshCw, Landmark, CreditCard as CreditCardIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { financialService } from '@services/financialService';
import { useUserPreferences, PREF_FIN_RECURRING } from '@hooks/useUserPreferences';
import type { Company } from '@/types/company';
import { formatCnpj } from '@/types/company';
import type {
  FinancialAccount,
  CreditCard,
  RecurringTransaction,
  RecurringType,
  RecurringDeleteMode,
} from '@/types/financial';
import {
  formatCurrencyBRL,
  buildCategoryOptions,
  activityTypeLabel,
  activityTypeBadgeColor,
} from './financialUtils';
import type { CategoryOptionGroup } from './financialUtils';
import {
  LoadingSpinner,
  ErrorBox,
  SuccessBox,
  PermissionFallback,
  ModalShell,
  CategorySelect,
  selectCls,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './financialShared';

interface RecurringPrefs extends Record<string, unknown> {
  company_id?: number;
  type?: RecurringType;
  target_id?: number;
}

// ─── Modal CRUD ─────────────────────────────────────────────────

interface RecurringModalProps {
  recurring: RecurringTransaction | null; // null = novo
  type: RecurringType;
  categoryGroups: CategoryOptionGroup[];
  onSave: (data: {
    description: string;
    transactionDay: number;
    amount: number;
    activityType: number;
    accountingPlanErpId: number | null;
  }) => Promise<void>;
  onClose: () => void;
}

const RecurringModal: React.FC<RecurringModalProps> = ({ recurring, type, categoryGroups, onSave, onClose }) => {
  const isEdit = recurring != null;
  const [description, setDescription] = useState(recurring?.description ?? '');
  const [transactionDay, setTransactionDay] = useState(recurring != null ? String(recurring.transactionDay) : '1');
  const [amount, setAmount] = useState(recurring != null ? String(Math.abs(recurring.amount)) : '');
  const [activityType, setActivityType] = useState<number>(recurring?.activityType ?? 0);
  const [accountingPlanErpId, setAccountingPlanErpId] = useState<number | null>(recurring?.accountingPlanErpId ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const parsedDay = parseInt(transactionDay, 10);
    const parsedAmount = parseFloat(amount.replace(',', '.'));
    if (!description.trim()) {
      setError('Informe a descrição');
      return;
    }
    if (isNaN(parsedDay) || parsedDay < 1 || parsedDay > 31) {
      setError('Informe um dia válido (1 a 31)');
      return;
    }
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Informe um valor positivo');
      return;
    }
    try {
      setSaving(true);
      await onSave({
        description: description.trim(),
        transactionDay: parsedDay,
        amount: parsedAmount,
        activityType,
        accountingPlanErpId,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar lançamento recorrente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? 'Editar Lançamento Recorrente' : 'Novo Lançamento Recorrente'}
      icon={<Repeat size={20} className="text-emerald-600" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} className={primaryBtnCls}>
            {saving ? <RefreshCw size={14} className="animate-spin" /> : null}
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
        Destino: <span className="font-semibold">{type === 1 ? 'Conta Corrente' : 'Cartão de Crédito'}</span>
      </div>
      <div>
        <label className={labelCls}>Descrição *</label>
        <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} autoFocus />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Dia do lançamento *</label>
          <input
            className={inputCls}
            type="number"
            min="1"
            max="31"
            step="1"
            value={transactionDay}
            onChange={(e) => setTransactionDay(e.target.value)}
          />
          <p className="text-xs text-slate-400 mt-1">Recomendado entre 1 e 28 para evitar meses curtos.</p>
        </div>
        <div>
          <label className={labelCls}>Valor (R$) *</label>
          <input
            className={inputCls}
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0,00"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Tipo *</label>
          <select className={selectCls} value={activityType} onChange={(e) => setActivityType(Number(e.target.value))}>
            <option value={0}>Saída</option>
            <option value={1}>Entrada</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Categoria</label>
          <CategorySelect groups={categoryGroups} value={accountingPlanErpId} onChange={setAccountingPlanErpId} />
        </div>
      </div>
      {/* O que a definição faz com o que já está lançado. É a dúvida que gerou
          o problema original: os valores aqui são estimativa, e o ajuste feito
          na Conta Corrente é que vale. */}
      <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
        Salvar atualiza os lançamentos dos próximos 12 meses que <strong>ainda não foram ajustados
        manualmente</strong>. Lançamentos já editados na Conta Corrente (ou na fatura do cartão)
        permanecem como estão.
      </p>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── Modal de exclusão (modo) ───────────────────────────────────

const DELETE_MODES: { value: RecurringDeleteMode; label: string }[] = [
  { value: 0, label: 'Somente a definição' },
  { value: 1, label: 'Definição + lançamentos gerados a partir do mês atual' },
  { value: 2, label: 'Definição + lançamentos gerados após o mês atual' },
];

const DeleteRecurringModal: React.FC<{
  recurring: RecurringTransaction;
  onConfirm: (mode: RecurringDeleteMode) => Promise<void>;
  onClose: () => void;
}> = ({ recurring, onConfirm, onClose }) => {
  const [mode, setMode] = useState<RecurringDeleteMode>(0);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      setDeleting(true);
      await onConfirm(mode);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ModalShell
      title="Excluir Lançamento Recorrente"
      icon={<Trash2 size={20} className="text-red-600" />}
      onClose={onClose}
      maxWidth="max-w-md"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={deleting} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {deleting ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {deleting ? 'Excluindo...' : 'Excluir'}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        Excluir <span className="font-semibold text-slate-800">{recurring.description}</span>. O que deseja remover?
      </p>
      <div className="space-y-2">
        {DELETE_MODES.map((option) => (
          <label
            key={option.value}
            className={clsx(
              'flex items-start gap-3 border rounded-lg p-3 text-sm cursor-pointer transition-colors',
              mode === option.value ? 'border-red-400 bg-red-50 text-red-800' : 'border-slate-200 text-slate-700 hover:bg-slate-50',
            )}
          >
            <input
              type="radio"
              name="delete-mode"
              className="mt-0.5 text-red-600 focus:ring-red-500"
              checked={mode === option.value}
              onChange={() => setMode(option.value)}
            />
            {option.label}
          </label>
        ))}
      </div>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── View Principal ─────────────────────────────────────────────

export const RecurringTransactionsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financial.recurring.write').hasPermission;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [type, setType] = useState<RecurringType>(1);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [cards, setCards] = useState<CreditCard[]>([]);
  const [targetId, setTargetId] = useState<number | null>(null); // idAccountsMain ou idCreditCards
  const [categoryGroups, setCategoryGroups] = useState<CategoryOptionGroup[]>([]);

  const [items, setItems] = useState<RecurringTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [formModal, setFormModal] = useState<{ recurring: RecurringTransaction | null } | null>(null);
  const [deleteModal, setDeleteModal] = useState<RecurringTransaction | null>(null);

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<RecurringPrefs>(PREF_FIN_RECURRING);
  const targetRestored = useRef(false);

  // Empresas — espera as preferências para não selecionar a primeira empresa antes da restaurada
  useEffect(() => {
    if (!prefsLoaded) return;
    if (prefs?.type === 1 || prefs?.type === 2) setType(prefs.type);
    companyService
      .getCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length === 0) return;
        const saved = list.find((c) => c.company_id === prefs?.company_id);
        setCompanyId(saved?.company_id ?? list[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  // Contas / cartões / plano de contas
  useEffect(() => {
    if (companyId == null) return;
    setAccounts([]);
    setCards([]);
    setTargetId(null);
    financialService
      .getAccounts(companyId)
      .then(setAccounts)
      .catch(() => setAccounts([]));
    financialService
      .getCreditCards(companyId)
      .then(setCards)
      .catch(() => setCards([]));
    financialService
      .getAccountingPlans(companyId)
      .then((tree) => setCategoryGroups(buildCategoryOptions(tree)))
      .catch(() => setCategoryGroups([]));
  }, [companyId]);

  // Ao trocar o tipo (ou carregar contas/cartões), seleciona o primeiro alvo disponível
  useEffect(() => {
    const ids = type === 1 ? accounts.map((a) => a.idAccountsMain) : cards.map((c) => c.idCreditCards);
    if (ids.length === 0) {
      setTargetId(null);
      return;
    }
    // Restaura a conta/cartão salvo apenas na primeira carga do tipo persistido.
    const savedId = prefs?.target_id;
    const canRestore =
      !targetRestored.current && prefs?.type === type && savedId != null && ids.includes(savedId);
    targetRestored.current = true;
    setTargetId(canRestore ? savedId : ids[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, accounts, cards]);

  // Persiste os filtros
  useEffect(() => {
    if (!prefsLoaded || companyId == null) return;
    savePrefs({ company_id: companyId, type, target_id: targetId ?? undefined });
  }, [prefsLoaded, companyId, type, targetId, savePrefs]);

  const loadItems = useCallback(async () => {
    if (companyId == null) return;
    setLoading(true);
    setError(null);
    try {
      const filters =
        targetId == null
          ? undefined
          : type === 1
            ? { idAccountsMain: targetId }
            : { idCreditCards: targetId };
      const list = await financialService.getRecurringTransactions(companyId, type, filters);
      setItems(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar lançamentos recorrentes');
    } finally {
      setLoading(false);
    }
  }, [companyId, type, targetId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // ── Ações ───────────────────────────────────────────────────

  const handleSave = async (data: {
    description: string;
    transactionDay: number;
    amount: number;
    activityType: number;
    accountingPlanErpId: number | null;
  }) => {
    if (companyId == null) throw new Error('Selecione uma empresa');
    if (targetId == null) throw new Error(type === 1 ? 'Selecione uma conta' : 'Selecione um cartão');

    const payload = {
      companyId,
      description: data.description,
      transactionDay: data.transactionDay,
      amount: data.amount,
      activityType: data.activityType,
      accountingPlanErpId: data.accountingPlanErpId,
      ...(type === 1 ? { idAccountsMain: targetId } : { idCreditCards: targetId }),
    };

    if (formModal?.recurring) {
      await financialService.updateRecurringTransaction(formModal.recurring.recurringTransactionId, payload);
      setNotice('Lançamento recorrente atualizado com sucesso');
    } else {
      await financialService.createRecurringTransaction(payload);
      setNotice('Lançamento recorrente criado com sucesso');
    }
    await loadItems();
  };

  const handleDelete = async (mode: RecurringDeleteMode) => {
    if (!deleteModal) return;
    await financialService.deleteRecurringTransaction(deleteModal.recurringTransactionId, mode);
    setNotice('Lançamento recorrente excluído com sucesso');
    await loadItems();
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <PermissionGuard permission="financial.recurring.read" fallback={<PermissionFallback label="os lançamentos recorrentes" />}>
      <div className="space-y-6">
        {/* Modais */}
        {formModal && (
          <RecurringModal
            recurring={formModal.recurring}
            type={type}
            categoryGroups={categoryGroups}
            onSave={handleSave}
            onClose={() => setFormModal(null)}
          />
        )}
        {deleteModal && (
          <DeleteRecurringModal recurring={deleteModal} onConfirm={handleDelete} onClose={() => setDeleteModal(null)} />
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Lançamentos Recorrentes</h1>
            <p className="text-sm text-slate-500 mt-1">Definições de lançamentos gerados automaticamente todo mês</p>
          </div>
          {canWrite && (
            <button className={primaryBtnCls} onClick={() => setFormModal({ recurring: null })} disabled={targetId == null}>
              <Plus size={16} /> Novo Recorrente
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Empresa</label>
              <select
                className={selectCls + ' w-64'}
                value={companyId ?? ''}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
              >
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {formatCnpj(c.document)} — {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Toggle Conta Corrente / Cartão */}
            <div>
              <label className={labelCls}>Destino</label>
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setType(1)}
                  className={clsx(
                    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
                    type === 1 ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  <Landmark size={15} /> Conta Corrente
                </button>
                <button
                  type="button"
                  onClick={() => setType(2)}
                  className={clsx(
                    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-l border-slate-300',
                    type === 2 ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  <CreditCardIcon size={15} /> Cartão de Crédito
                </button>
              </div>
            </div>

            <div>
              <label className={labelCls}>{type === 1 ? 'Conta' : 'Cartão'}</label>
              <select
                className={selectCls + ' w-64'}
                value={targetId ?? ''}
                onChange={(e) => setTargetId(e.target.value ? Number(e.target.value) : null)}
              >
                {type === 1 ? (
                  <>
                    {accounts.length === 0 && <option value="">Nenhuma conta disponível</option>}
                    {accounts.map((a) => (
                      <option key={a.idAccountsMain} value={a.idAccountsMain}>
                        {a.accountsDescription}
                        {a.bankName ? ` — ${a.bankName}` : ''}
                      </option>
                    ))}
                  </>
                ) : (
                  <>
                    {cards.length === 0 && <option value="">Nenhum cartão disponível</option>}
                    {cards.map((card) => (
                      <option key={card.idCreditCards} value={card.idCreditCards}>
                        {card.description}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>
        </div>

        {notice && <SuccessBox message={notice} onDismiss={() => setNotice(null)} />}
        {error && <ErrorBox message={error} onRetry={loadItems} />}
        {loading && <LoadingSpinner />}

        {!loading && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                    <th className="text-center py-2 px-3 font-semibold text-slate-600">Dia</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Categoria</th>
                    <th className="text-center py-2 px-3 font-semibold text-slate-600">Tipo</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                    {canWrite && <th className="text-right py-2 px-3 font-semibold text-slate-600 w-24">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={canWrite ? 6 : 5} className="py-10 text-center text-slate-400">
                        Nenhum lançamento recorrente cadastrado
                      </td>
                    </tr>
                  )}
                  {items.map((item) => (
                    <tr key={item.recurringTransactionId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-700">{item.description}</td>
                      <td className="py-2 px-3 text-center text-slate-700">{item.transactionDay}</td>
                      <td className="py-2 px-3 text-slate-500">{item.categoryLabel ?? '—'}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={clsx('inline-flex text-xs rounded-full px-2 py-0.5 font-medium', activityTypeBadgeColor(item.activityType))}>
                          {activityTypeLabel(item.activityType)}
                        </span>
                      </td>
                      <td className={clsx('py-2 px-3 text-right font-medium whitespace-nowrap', item.activityType === 1 ? 'text-emerald-600' : 'text-red-600')}>
                        {formatCurrencyBRL(Math.abs(item.amount))}
                      </td>
                      {canWrite && (
                        <td className="py-2 px-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                              title="Editar"
                              onClick={() => setFormModal({ recurring: item })}
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                              title="Excluir"
                              onClick={() => setDeleteModal(item)}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default RecurringTransactionsView;
